import apiClient from './apiClient';

/**
 * Per-user voice preferences (wake-word + mode), stored under the
 * existing `users.preferences` jsonb so it's per-user not per-device.
 *
 * Frontend role:
 *   - On post-auth boot: GET, then merge into `useVoiceStore.settings`
 *     so an iPad inherits the doctor's desktop choice on first launch.
 *   - On settings change: PUT (debounced) so the next device the doctor
 *     signs in on starts with the same toggles.
 *
 * Backend default (when the user has never set anything): wake-word OFF,
 * mode 'always-listening' — matches `useVoiceStore.defaultSettings`.
 */

export interface VoicePrefsDTO {
  wakeWordEnabled: boolean;
  wakeWordMode: 'always-listening' | 'push-to-activate';
  /** Scribe transcription language; defaults to 'en'. 'auto' is bilingual
   * auto-detect (less accurate). */
  sttLanguage: 'en' | 'ar' | 'auto';
  /** Default encounter capture mode; defaults to 'visit'. 'visit' = doctor +
   * patient with speaker labels; 'dictate' = single voice, faster, no labels. */
  defaultCaptureMode: 'visit' | 'dictate';
  /** Higher-accuracy first draft: wait for the full-audio re-transcription
   * before generating the note. Defaults to false (speed-first). */
  preferRefinedForNote: boolean;
}

const BASE = '/users/me/voice-prefs';

export const voicePrefsApi = {
  async get(): Promise<VoicePrefsDTO> {
    const res = await apiClient.get<VoicePrefsDTO>(BASE);
    return res.data;
  },

  async put(next: Partial<VoicePrefsDTO>): Promise<VoicePrefsDTO> {
    const res = await apiClient.put<VoicePrefsDTO>(BASE, next);
    return res.data;
  },
};
